<template>
  <v-skeleton-loader class="mx-auto" :loading="loading" type="table">
    <v-card outlined>
      <v-card-title>Risk Profile</v-card-title>
      <v-simple-table>
        <template v-slot:default>
          <tbody v-for="info in reportData" :key="info.ID">
            <tr>
              <td>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon small color="grey lighten-1">mdi-information</v-icon>
                    </v-btn>
                  </template>
                  <span>
                    A concurrent total cost risk for this patient for the observation period.
                    <br />Based upon a regression model against a reference population (with a mean of
                    1.0), the predicted value is expressed as a relative weight.
                  </span>
                </v-tooltip>
                <span>Reference Inherent Risk</span>
              </td>

              <td>
                <v-chip color="secondary darken-2">
                  {{
                  info.REFERENCE_INHERENT_RISK.toFixed(2)
                  }}
                </v-chip>
              </td>
              <td>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon small color="grey lighten-1">mdi-information</v-icon>
                    </v-btn>
                  </template>
                  <span>
                    Unscaled concurrent risk that is rescaled so that the mean across the population
                    is 1.0
                    <br />(i.e., unscaled_concurrent_risk for this patient / average of
                    unscaled_concurrent_risk for all patients).
                  </span>
                </v-tooltip>
                <span>Local Inherent Risk</span>
              </td>
              <td>
                <v-chip color="secondary darken-2">{{ info.LOCAL_INHERENT_RISK.toFixed(2) }}</v-chip>
              </td>
            </tr>
            <tr>
              <td>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon small color="grey lighten-1">mdi-information</v-icon>
                    </v-btn>
                  </template>
                  <span>
                    An estimate of concurrent resource use associated with a given ACG based on a
                    reference database and expressed as a relative value.
                    <br />Each patient is
                    assigned a weight based on their ACG Cd.
                  </span>
                </v-tooltip>
                <span>Reference AI Inherent Risk</span>
              </td>
              <td>
                <v-chip color="secondary darken-2">
                  {{
                  info.REFERENCE_AI_INHERENT_RISK.toFixed(2)
                  }}
                </v-chip>
              </td>
              <td>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon small color="grey lighten-1">mdi-information</v-icon>
                    </v-btn>
                  </template>
                  <span>
                    A concurrent weight assigned to this patient based upon their ACG Cd using local
                    cost data.
                    <br />The weight for each ACG is calculated as the simple average total
                    cost of all individuals assigned to each category divided by the average total
                    cost of all individuals in the source data file.
                  </span>
                </v-tooltip>
                <span>Local AI Inherent Risk</span>
              </td>
              <td>
                <v-chip color="secondary darken-2">
                  {{
                  info.LOCAL_ai_INHERENT_RISK.toFixed(2)
                  }}
                </v-chip>
              </td>
            </tr>
            <tr>
              <td>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon small color="grey lighten-1">mdi-information</v-icon>
                    </v-btn>
                  </template>
                  <span>
                    Probability that this patient will be in the top 20% of users in the population
                    for four consecutive half year periods.
                  </span>
                </v-tooltip>
                <span>Persistent High User Probability</span>
              </td>

              <td>
                <v-chip color="secondary darken-2">
                  {{
                  nullCheckAndFormat(info.PERSISTENT_HIGH_USER_PROBABILITY)
                  }}
                </v-chip>
              </td>

              <td>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon small color="grey lighten-1">mdi-information</v-icon>
                    </v-btn>
                  </template>
                  <span>
                    A concurrent weight assigned to this patient based upon their ACG Cd using local
                    cost data.
                    <br />The weight for each ACG is calculated as the simple average total cost of all
                    individuals assigned to each category divided by the average total cost of all
                    individuals in the source data file.
                  </span>
                </v-tooltip>
                <span>Local AI Marker Risk</span>
              </td>
              <td>
                <v-chip color="secondary darken-2">{{ info.LOCAL_AI_MARKER_RISK.toFixed(2) }}</v-chip>
              </td>
            </tr>
            <tr>
              <td>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon small color="grey lighten-1">mdi-information</v-icon>
                    </v-btn>
                  </template>
                  <span>
                    Unscaled Predicted Risk for Total Cost—the estimated total costs (including
                    pharmacy costs) for this patient for the year following the observation period.
                    <br />Based upon a reference database (with a mean of 1.0), the predicted value is
                    expressed as a relative weight. The value is based on best model selection. The
                    model used can be found in the Summary Statistics.
                  </span>
                </v-tooltip>
                <span>Reference Total Cost Risk</span>
              </td>

              <td>
                <v-chip color="secondary darken-2">
                  {{
                  nullCheckAndFormat(info.REFERENCE_TOTAL_COST_RISK)
                  }}
                </v-chip>
              </td>
              <td>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon small color="grey lighten-1">mdi-information</v-icon>
                    </v-btn>
                  </template>
                  <span>
                    The Unscaled Total Cost Predicted Risk rescaled so that the local population mean
                    is 1.0
                    <br />(i.e., unscaled_total_cost_predicted_risk for this patient / average
                    of unscaled_total_cost_predicted_risk for all patients)..
                  </span>
                </v-tooltip>
                <span>Local Total Cost Risk</span>
              </td>
              <td>
                <v-chip color="secondary darken-2">
                  {{
                  nullCheckAndFormat(info.LOCAL_TOTAL_COST_RISK)
                  }}
                </v-chip>
              </td>
            </tr>
            <tr>
              <td>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon small color="grey lighten-1">mdi-information</v-icon>
                    </v-btn>
                  </template>
                  <span>
                    Reference Probability Score for high pharmacy cost—the probability that this
                    patient will have pharmacy costs in the top 5% of the reference population in the
                    year following the observation period.
                    <br />Determined by a logistic regression of the reference population
                  </span>
                </v-tooltip>
                <span>Reference Top 5% Cost Probability</span>
              </td>

              <td>
                <v-chip color="secondary darken-2">
                  {{
                  nullCheckAndFormat(info.REFERENCE_TOP_5_PERCENT_COST_PROBABILTY)
                  }}
                </v-chip>
              </td>
              <td>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon small color="grey lighten-1">mdi-information</v-icon>
                    </v-btn>
                  </template>
                  <span>
                    Unscaled concurrent risk that is rescaled so that the mean across the population
                    is 1.0
                    <br />(i.e., unscaled_concurrent_risk for this patient / average of
                    unscaled_concurrent_risk for all patients).
                  </span>
                </v-tooltip>
                <span>Local Top 5% Cost Probability</span>
              </td>
              <td>
                <v-chip color="secondary darken-2">
                  {{
                  nullCheckAndFormat(info.LOCAL_TOP_5_PERCENT_COST_PROBABILITY)
                  }}
                </v-chip>
              </td>
            </tr>
            <tr>
              <td>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon small color="grey lighten-1">mdi-information</v-icon>
                    </v-btn>
                  </template>
                  <span>
                    A concurrent total cost risk for this patient for the observation period.
                    <br />Based upon a regression model against a reference population (with a mean of
                    1.0), the predicted value is expressed as a relative weight.
                  </span>
                </v-tooltip>
                <span>Reference RX Cost Risk</span>
              </td>

              <td>
                <v-chip color="secondary darken-2">
                  {{
                  nullCheckAndFormat(info.REFERENCE_RX_COST_RISK)
                  }}
                </v-chip>
              </td>
              <td>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon small color="grey lighten-1">mdi-information</v-icon>
                    </v-btn>
                  </template>
                  <span>
                    The Unscaled Pharmacy Cost Predicted Risk rescaled so that the local population
                    mean is 1.0
                    <br />(i.e., unscaled_pharmacy_cost_predicted_risk for this patient /
                    average of unscaled_pharmacy_cost_predicted_risk for all patients).
                  </span>
                </v-tooltip>
                <span>Local RX Cost Risk</span>
              </td>
              <td>
                <v-chip color="secondary darken-2">
                  {{
                  nullCheckAndFormat(info.LOCAL_RX_COST_RISK)
                  }}
                </v-chip>
              </td>
            </tr>
            <tr>
              <td>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon small color="grey lighten-1">mdi-information</v-icon>
                    </v-btn>
                  </template>
                  <span>
                    A concurrent total cost risk for this patient for the observation period.
                    <br />Based upon a regression model against a reference population (with a mean of
                    1.0), the predicted value is expressed as a relative weight.
                  </span>
                </v-tooltip>
                <span>Reference Top 5% RX Cost Probability</span>
              </td>

              <td>
                <v-chip color="secondary darken-2">
                  {{
                  nullCheckAndFormat(info.REFERENCE_TOP_5_PERCENT_RX_COST_PROBABILITY)
                  }}
                </v-chip>
              </td>
              <td>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon small color="grey lighten-1">mdi-information</v-icon>
                    </v-btn>
                  </template>
                  <span>
                    Rank Probability Score for high pharmacy cost—the probability that this patient
                    will have pharmacy costs in the top 5% of this population in the year following
                    the observation period.
                    <br />Determined by ranking the Unscaled Pharmacy Cost
                    Predicted Risk within the local population.
                  </span>
                </v-tooltip>
                <span>Local Top 5% RX Cost Probability</span>
              </td>
              <td>
                <v-chip color="secondary darken-2">
                  {{
                  info.LOCAL_TOP_5_PERCENT_RX_COST_PROBABILITY
                  }}
                </v-chip>
              </td>
            </tr>
            <tr>
              <td>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon small color="grey lighten-1">mdi-information</v-icon>
                    </v-btn>
                  </template>
                  <span>
                    Indicates that this patient has high risk of an unexpectedly high
                    <br />pharmacy
                    cost in the year following the observation period (Y/N).
                  </span>
                </v-tooltip>
                <span>High Risk Unexpected RX Cost</span>
              </td>

              <td>
                <v-chip color="secondary darken-2">{{ info.HIGH_RISK_UNEXPECTED_RX_COST }}</v-chip>
              </td>
              <td>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon small color="grey lighten-1">mdi-information</v-icon>
                    </v-btn>
                  </template>
                  <span>
                    Probability that this patient will have unexpectedly high pharmacy costs in the
                    year following the observation period.
                  </span>
                </v-tooltip>
                <span>High Unexpected RX Cost Probability</span>
              </td>
              <td>
                <v-chip color="secondary darken-2">
                  {{
                  nullCheckAndFormat(info.HIGH_UNEXPECTED_RX_COST_PROBABILITY)
                  }}
                </v-chip>
              </td>
            </tr>
            <tr>
              <td>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon small color="grey lighten-1">mdi-information</v-icon>
                    </v-btn>
                  </template>
                  <span>A sum of the gaps in medication availability across all treated conditions</span>
                </v-tooltip>
                <span>Total RX Gaps</span>
              </td>
              <td>
                <v-chip color="secondary darken-2">{{ info.TOTAL_RX_GAPS }}</v-chip>
              </td>

              <td>
                <v-tooltip small top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon small color="grey lighten-1">mdi-information</v-icon>
                    </v-btn>
                  </template>
                  <span>
                    A concurrent weight assigned to this patient based upon their age-sex mix using
                    local cost data.
                    <br />The weight for each age band and gender is calculated as the simple average total
                    cost of all individuals assigned to each category divided by the average total
                    cost of all individuals in the source data file.
                  </span>
                </v-tooltip>
                <span>Local Age Gender Inherent Risk</span>
              </td>
              <td>
                <v-chip color="secondary darken-2">
                  {{
                  info.LOCAL_AGE_GENDER_INHERENT_RISK.toFixed(2)
                  }}
                </v-chip>
              </td>
            </tr>

            <tr>
              <td>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon small color="grey lighten-1">mdi-information</v-icon>
                    </v-btn>
                  </template>
                  <span>A total cost in RX</span>
                </v-tooltip>
                <span>Pharmacy Cost</span>
              </td>
              <td>
                <v-chip color="secondary darken-2">{{ info.PHARMACY_COST.toFixed(2) }}</v-chip>
              </td>

              <td>
                <v-tooltip small top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon small color="grey lighten-1">mdi-information</v-icon>
                    </v-btn>
                  </template>
                  <span>All total cost</span>
                </v-tooltip>
                <span>Total Cost</span>
              </td>
              <td>
                <v-chip color="secondary darken-2">
                  {{
                  info.TOTAL_COST ? info.TOTAL_COST : "0"
                  }}
                </v-chip>
              </td>
            </tr>
            <tr>
              <td>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon small color="grey lighten-1">mdi-information</v-icon>
                    </v-btn>
                  </template>
                  <span>A total cost in RX</span>
                </v-tooltip>
                <span>RX Cost Band</span>
              </td>
              <td>
                <v-chip color="secondary darken-2">{{ info.RX_COST_BAND }}</v-chip>
              </td>

              <td>
                <v-tooltip small top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon small color="grey lighten-1">mdi-information</v-icon>
                    </v-btn>
                  </template>
                  <span>All total cost</span>
                </v-tooltip>
                <span>Total Cost Band</span>
              </td>
              <td>
                <v-chip color="secondary darken-2">
                  {{
                  info.TOTAL_COST_BAND ? info.TOTAL_COST_BAND : "0"
                  }}
                </v-chip>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-card>
  </v-skeleton-loader>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ReferenceVsLocalRisk',
  props: [
    'value',
    'patientID',
    'organizations',
    'insurance',
    'startDate',
    'endDate',
    'providers',
    'groups',
    'locations',
  ],
  data() {
    return {
      loading: false,
      dialog: false,
      reportData: [],
      fireReport: false,
    };
  },
  created() {
    this.getReport();
  },
  methods: {
    async getReport() {
      const { startDate } = this;
      const { patientID } = this;
      const { endDate } = this;
      const { insurance } = this;
      const organizations = this.organization;
      const { groups } = this;
      const provNums = this.providers;
      const locs = this.locations;

      let providers = [];
      let locations = [];

      if (provNums.length > 0) {
        provNums.forEach((prov) => {
          if (providers.indexOf(prov.Center_PCP_Number) === -1) {
            providers.push(prov.Center_PCP_Number);
          }
        });
      } else {
        providers = [];
      }

      if (locs.length > 0) {
        locs.forEach((loca) => {
          if (locations.indexOf(loca.Center_Location) === -1) {
            locations.push(loca.Center_Location);
          }
        });
      } else {
        locations = [];
      }

      // Check for pending request and if so cancel old one for new incoming
      this.cancelSearch();
      // Set the CancelToken
      this.cancelSource = axios.CancelToken.source();

      if (insurance.length !== 0) {
        this.loading = true;
        this.reportData = [];

        const response = axios
          .get(
            `api/ai/modal/refvslocalrisk/orgs=${organizations}/groups=${groups}/providers=${providers}/locs=${locations}/${insurance}/${startDate}/${endDate}/${patientID}`,
            { cancelToken: this.cancelSource.token },
          )
          .then((response) => {
            this.reportData = response.data;
            this.cancelSource = null;
            this.loading = false;
          });
      }
    },
    nullCheckAndFormat(field) {
      if (field !== null && field !== '') {
        return field.toFixed(2);
      }
      return '0.00';
    },
    cancelSearch() {
      if (this.cancelSource) {
        this.cancelSource.cancel('Start new search, stop active search');
      }
    },
  },

  computed: {
    showDialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },
  watch: {
    // Watch every dropdown and run report again on change
    async groups() {
      this.getReport();
    },
    async providers() {
      this.getReport();
    },
    async locations() {
      this.getReport();
    },
    async startDate() {
      this.getReport();
    },
    async endDate() {
      this.getReport();
    },
    async patientID() {
      this.getReport();
    },
  },
};
</script>

<style>
.code-list {
  list-style-type: none;
  margin: 0 !important;
  padding: 0 !important;
}
</style>
